/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCustomContent } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { VisualizedKeyPhrasesResult } from 'src/languageStudio/appShell/prebuiltDemos/keyPhrasesDemo/VisualizedKeyPhrasesResult';
import { textAnalyticsDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    keyPhrasesApiReferenceLink,
    keyPhrasesDocLink,
    keyPhrasesOverviewLink,
    keyPhrasesSamplesLink,
    keyPhrasesSdkLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { keyPhrasesDemoSupportedLanguages, Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { useTextAnalyticsCapabilities } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { KeyPhrasesDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    pageHeader: { id: 'languageStudio.keyPhrasesDemoPage.pageHeader', defaultMessage: 'Extract key phrases' },
    capabilityDescription: {
        id: 'languageStudio.keyPhrasesDemoPage.capabilityDescription',
        defaultMessage: `The Key Phrase Extraction API evaluates unstructured text, and for each JSON
        document, returns a list of key phrases. This capability is useful if you need to quickly
        identify the main points from your text.`
    },
    emptyState: {
        id: 'languageStudio.keyPhrasesDemoPage.emptyState',
        defaultMessage: `Your results will appear here once you enter or upload some text and press Run.
        The file format needs to be .txt`
    }
});

/**
 * Displays the Key Phrases demo page.
 */
export const KeyPhrasesDemoPage = observer(() => {
    const { localize } = useLocalization();

    const { tracker, resetResults, getCurlCommand, keyPhrases, getKeyPhrases, detectedLanguages } = useTextAnalyticsCapabilities();

    const [document, setDocument] = React.useState('');
    const [language, setLanguage] = React.useState<Language['code']>('en');
    const [languageAutoDetection, setLanguageAutoDetection] = React.useState(false);
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);

    const handleDocumentChange = (newDocument: string) => {
        if (keyPhrases) {
            resetResults();
        }
        setDocument(newDocument);
    };

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent('Key Phrases');
        }

        getKeyPhrases({ document, language, sampleTitle: selectedSampleTitle, autoDetectLanguage: languageAutoDetection });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                featureName="Key Phrases"
                demoVersion={LuisConstants.Demos.ApiVersions.GA}
                getSdkLink={keyPhrasesSdkLink}
                learnMoreLink={keyPhrasesOverviewLink}
                documentationLink={keyPhrasesDocLink}
                supportedPlatforms={['docker', 'cloud']}
                demoTitle={localize(messages.pageHeader)}
                gitHubSamplesLink={keyPhrasesSamplesLink}
                demoDescription={localize(messages.capabilityDescription)}
            />

            <DocumentInputStep
                document={document}
                featureName="Key Phrases"
                selectedLanguage={language}
                onLanguageChange={setLanguage}
                acceptedResourceType="TextAnalytics"
                samplesList={textAnalyticsDemoSamples}
                onDocumentChange={handleDocumentChange}
                selectedSampleTitle={selectedSampleTitle}
                languagesList={keyPhrasesDemoSupportedLanguages}
                supportedRegionLink={keyPhrasesApiReferenceLink}
                onLanguageAutoDetectionClick={setLanguageAutoDetection}
                onSampleClick={sample => setSelectedSampleTitle(sample?.title)}
                isTextRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}>
                <DemoPageAgreement
                    featureName="Key Phrases"
                    onRunDemoClick={handleRunDemo}
                    isRunDisabled={!document.length}
                    acceptedResourceType="TextAnalytics"
                    pricingLink={textAnalyticsPricingLink}
                />
            </DocumentInputStep>

            <ResultsStep<KeyPhrasesDocuments>
                results={keyPhrases}
                featureName="Key Phrases"
                isLoading={tracker.isRunning}
                emptyStateMessage={localize(messages.emptyState)}>
                <VisualizedKeyPhrasesResult
                    document={document}
                    detectedLanguage={detectedLanguages}
                    keyPhrases={keyPhrases?.documents[0].keyPhrases}
                    isLabelerRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}
                    isLabelerWordTokenized={isDocumentSwaggerWordTokenized(
                        detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                    )}
                />
            </ResultsStep>

            <NextStepsStep
                featureName="Key Phrases"
                sdkLink={keyPhrasesSdkLink}
                isResultsEmpty={!keyPhrases}
                documentationLink={keyPhrasesDocLink}
                sampleCodeLink={keyPhrasesSamplesLink}
                demosToDisplay={['sentiment', 'ner', 'pii']}
                curlCommand={getCurlCommand({ endpoint: 'KeyPhraseExtraction', text: document, languageAutoDetection, language })}
            />
        </Stack>
    );
});
